// Template-based AI Narrative System
// These templates generate human-readable insights from data

export interface TemplateContext {
  [key: string]: string | number | boolean | string[] | Record<string, unknown> | undefined
}

export interface NarrativeTemplate {
  id: string
  category: 'fleet' | 'asset' | 'anomaly' | 'maintenance' | 'executive'
  condition?: (data: TemplateContext) => boolean
  template: string
  priority: number
}

// Template interpolation function
export function interpolate(template: string, context: TemplateContext): string {
  return template.replace(/\{(\w+)\}/g, (match, key) => {
    const value = context[key]
    if (value === undefined) return match
    if (Array.isArray(value)) return value.join(', ')
    if (typeof value === 'object') return JSON.stringify(value)
    return String(value)
  })
}

// Conditional template selection
export function selectTemplate(
  templates: NarrativeTemplate[],
  context: TemplateContext,
  category?: string
): NarrativeTemplate | null {
  const filtered = templates
    .filter((t) => (!category || t.category === category))
    .filter((t) => !t.condition || t.condition(context))
    .sort((a, b) => b.priority - a.priority)

  return filtered[0] || null
}

// Generate narrative from template
export function generateNarrative(
  templates: NarrativeTemplate[],
  context: TemplateContext,
  category?: string
): string | null {
  const template = selectTemplate(templates, context, category)
  if (!template) return null
  return interpolate(template.template, context)
}

// Number formatting helpers for templates
export function formatPercent(value: number, decimals = 1): string {
  return `${value.toFixed(decimals)}%`
}

export function formatNumber(value: number, decimals = 0): string {
  return value.toLocaleString('en-US', { maximumFractionDigits: decimals })
}

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(value)
}

export function formatDuration(hours: number): string {
  if (hours < 1) return `${Math.round(hours * 60)} minutes`
  if (hours < 24) return `${hours.toFixed(1)} hours`
  const days = hours / 24
  return `${days.toFixed(1)} days`
}

// Trend description helper
export function describeTrend(current: number, previous: number): string {
  const change = ((current - previous) / previous) * 100
  if (Math.abs(change) < 1) return 'stable'
  if (change > 10) return 'significantly improving'
  if (change > 0) return 'improving'
  if (change < -10) return 'significantly declining'
  return 'declining'
}

// Priority descriptor
export function describePriority(score: number): string {
  if (score >= 0.9) return 'CRITICAL'
  if (score >= 0.7) return 'HIGH'
  if (score >= 0.4) return 'MEDIUM'
  return 'LOW'
}

// Time until description
export function describeTimeUntil(hours: number): string {
  if (hours < 0) return 'overdue'
  if (hours < 1) return 'within the next hour'
  if (hours < 24) return `within ${Math.ceil(hours)} hours`
  if (hours < 48) return 'tomorrow'
  if (hours < 168) return `within ${Math.ceil(hours / 24)} days`
  return `within ${Math.ceil(hours / 168)} weeks`
}
